import React, { useEffect, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, LinearProgress, Skeleton, Stack, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ForecastReviewGrid } from "../../Components/ForecastReviewGrid";
import ProductMultiSelection from "../../Components/ProductMultiSelection";
import { useForecastReviewContext } from "../../Contexts/forecastReviewContext";
import { useDistributorsContext } from "../../Contexts/distributorsContext";
import { useInternalSkuContext } from "../../Contexts/internalSkuContext";
import { CustomerType } from "../../Enums/CustomerType";
import { SkuFulfillmentMethod } from "../../Enums/SkuFulfillmentMethod";
import { useProductsContext } from "../../Contexts/productsContext";
import DistributorMultiselection from "../../Components/DistributorMultiSelection";
import InternalSkuMultiSelection from "../../Components/InternalSkuMultiSelection";
import PropTypes from 'prop-types';
import { SKU_LOCAL_STORAGE_KEY, DISTRIBUTOR_LOCAL_STORAGE_KEY, INTERNAL_SKU_LOCAL_STORAGE_KEY } from "../../Utilities/constants.ts";
import FileUpload from "../../Components/FileUpload";
import TicketRequestMenu from "../../Components/TicketRequestMenu";
import InfoDialog from './dialogs/InfoDialog';
import ErrorDialog from './dialogs/ErrorDialog';
import ImportSuccessDialog from './dialogs/ImportSuccessDialog';
import { buildForecastUploadTemplateCsv } from "../../Utilities/forecastUploadTemplateCsv";

export const ForecastReview = ({
  editableWeeksStartIndex,
  editableWeeksEndIndex,
  startWeek,
  endWeek
}) => {
  const { selectedDistributors, setSelectedDistributors } = useDistributorsContext();
  const { selectedProducts, setSelectedProducts } = useProductsContext();
  const { selectedInternalSkus, setSelectedInternalSkus } = useInternalSkuContext();
  const { isReady: areForecastReviewItemsReady, forecastReviewItems, getFormattedOriginalForecastReviewItems, setForecastReviewItems, get, edited, setEdited, SaveForecastAdjustmentChanges } = useForecastReviewContext();
  const [exporting, setExporting] = useState(false);
  const [sending, setSending] = useState(false);
  const [importSuccessDialogOpen, setImportSuccessDialogOpen] = useState(false);
  const [importSuccessData, setImportSuccessData] = useState(null);
  const [replenExpanded, setReplenExpanded] = useState(true);
  const [orderOnlyExpanded, setOrderOnlyExpanded] = useState(true);
  const [isDistributorDropdownOpen, setIsDistributorDropdownOpen] = useState(false);
  const isSplitView = forecastReviewItems.some(item => item.fulfillmentMethodId != null);
  const bothExpanded = replenExpanded && orderOnlyExpanded;
  const expandedHeight = bothExpanded ? "400px" : "calc(100vh - 400px)";

  useEffect(() => {
    const storedSku = localStorage.getItem(SKU_LOCAL_STORAGE_KEY)
    if (storedSku) {
      setSelectedProducts(JSON.parse(storedSku));
    }

    const storedDistributor = localStorage.getItem(DISTRIBUTOR_LOCAL_STORAGE_KEY)
    if (storedDistributor && storedDistributor !== "undefined") {
      setSelectedDistributors(JSON.parse(storedDistributor));
      get();
    }

    const storedInternalSku = localStorage.getItem(INTERNAL_SKU_LOCAL_STORAGE_KEY);
    if (storedInternalSku) {
      setSelectedInternalSkus(JSON.parse(storedInternalSku));
    }
  }, []);

  useEffect(() => {
    if (selectedDistributors && !isDistributorDropdownOpen) {
      get();
    }
    localStorage.setItem(DISTRIBUTOR_LOCAL_STORAGE_KEY, JSON.stringify(selectedDistributors));
  }, [selectedDistributors]);

  const exportForecast = () => {
    setExporting(true);

    let distributors = "";
    selectedDistributors.forEach(distributor => distributors += `distributorIds=${distributor.distributorId}&`);

    let products = "";
    selectedProducts.forEach(product => products += `productSKUs=${product.sku}&`);

    window.open(`api/Distributor/Forecasts/csvFile?${distributors}${products}`);

    setExporting(false);
  };

  useEffect(() => {
    if (forecastReviewItems?.length === 0) {
      const selectedSKUs = selectedProducts?.map(selectedProduct => selectedProduct.sku);
      let items = getFormattedOriginalForecastReviewItems();
      if (selectedSKUs?.length > 0) {
        items = items.filter(forecastReviewItem => selectedSKUs.includes(forecastReviewItem.product.sku));
      }
      if (selectedInternalSkus?.length > 0) {
        items = items.filter((forecastReviewItem) =>
          selectedInternalSkus.some(
            (s) =>
              s.internalSku === forecastReviewItem.customerPartNumber &&
              s.sapId === forecastReviewItem.distributorSapId &&
              s.sku === forecastReviewItem.product?.sku
          )
        );
      }
      setForecastReviewItems(items);
      localStorage.setItem(SKU_LOCAL_STORAGE_KEY, JSON.stringify(selectedProducts));
    }
  }, [selectedProducts, selectedInternalSkus, areForecastReviewItemsReady, setSelectedProducts]);

  return (
    <React.Fragment>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center" sx={{ pl: 2, pt: 2, pr: 2 }}>
        <DistributorMultiselection
          customerType={CustomerType.VMI}
          filterByCanOrderBeer={true}
          onOpen={() => setIsDistributorDropdownOpen(true)}
          onClose={() => { setIsDistributorDropdownOpen(false); get(); }}
        />
        <InternalSkuMultiSelection />
        <ProductMultiSelection />
        <Stack direction="row" spacing={2} alignItems="center" sx={{ ml: 'auto' }}>
          {process.env.REACT_APP_ENABLE_FORECAST_UPLOAD === 'true' && (
            <FileUpload
              uploadUrl="api/Distributor/Forecasts/import"
              buttonText="Upload Forecasts"
              accept=".csv"
              templateDownloadFileName="ForecastUploadTemplate.csv"
              templateDownloadTooltip="Download template"
              getTemplateDownloadContent={() => buildForecastUploadTemplateCsv({ numberOfWeeks: 8 })}
              onSuccess={(responseData) => {
                get();
                if (responseData && (responseData.message != null || responseData.updatedCount != null)) {
                  setImportSuccessData(responseData);
                  setImportSuccessDialogOpen(true);
                }
              }}
              InfoDialogComponent={InfoDialog}
              ErrorDialogComponent={ErrorDialog}
              size="small"
            />
          )}
          <Button
            variant="outlined"
            disabled={forecastReviewItems.length === 0}
            onClick={() => {
              setForecastReviewItems([]);
              get();
            }}
          >
            Reset
          </Button>
          <Button
            variant="outlined"
            disabled={forecastReviewItems.length === 0 && !exporting}
            onClick={exportForecast}
          >
            Export
          </Button>
          <TicketRequestMenu />
        </Stack>
      </Stack>

      {!selectedDistributors || selectedDistributors.length === 0 || !areForecastReviewItemsReady ? (
        selectedDistributors && selectedDistributors.length > 0 && !areForecastReviewItemsReady ? (
          <Skeleton variant="rectangular" height={400} />
        ) : (
          <center style={{ padding: 25 }}>
            <h4>Please select a distributor to review their forecast</h4>
          </center>
        )
      ) : forecastReviewItems.length === 0 ? (
        <center style={{ padding: 25 }}>
          <h4>There is no forecast data for the selected filter</h4>
          <h5>
            If you believe this is an error, please contact your business
            partner or the helpdesk at
            <a href="mailto: customerservice@bostonbeer.com"> customerservice@bostonbeer.com</a>
          </h5>
        </center>
      ) : isSplitView ? (
        <Box sx={{ padding: 2, pb: 8 }}>
          <Alert sx={{ mb: 2 }} severity="warning">
            Note that the forecasted quantity displayed is representative of the forecast for both direct and any partnered indirect wholesalers. Please ensure any forecast adjustments also account for indirect wholesalers.
          </Alert>
          <Accordion expanded={replenExpanded} onChange={(_, isExpanded) => { setReplenExpanded(isExpanded); if (!isExpanded) setOrderOnlyExpanded(true); }} sx={{ mb: 4 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#ffffff' }}>
              <Typography variant="h6"><strong>Replenishable</strong></Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0, overflow: 'hidden' }}>
              <ForecastReviewGrid
                editableWeeksStartIndex={editableWeeksStartIndex}
                editableWeeksEndIndex={editableWeeksEndIndex}
                startWeek={startWeek}
                endWeek={endWeek}
                isHistorical={false}
                filterByFulfillmentMethodId={SkuFulfillmentMethod.REPLENISHABLE}
                hideSubmit
                hideAlerts
                height={expandedHeight}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion expanded={orderOnlyExpanded} onChange={(_, isExpanded) => { setOrderOnlyExpanded(isExpanded); if (!isExpanded) setReplenExpanded(true); }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#ffffff' }}>
              <Typography variant="h6"><strong>Order Only</strong></Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0, overflow: 'hidden' }}>
              <ForecastReviewGrid
                editableWeeksStartIndex={editableWeeksStartIndex}
                editableWeeksEndIndex={editableWeeksEndIndex}
                startWeek={startWeek}
                endWeek={endWeek}
                isHistorical={false}
                filterByFulfillmentMethodId={SkuFulfillmentMethod.ORDER_ONLY}
                hideSubmit
                hideAlerts
                height={expandedHeight}
              />
            </AccordionDetails>
          </Accordion>
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              backgroundColor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              px: 2,
              py: 1,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Button
                variant="contained"
                onClick={async () => {
                  setSending(true);
                  await SaveForecastAdjustmentChanges();
                  setSending(false);
                  setEdited(false);
                  setSelectedProducts([]);
                }}
                disabled={!edited || sending}
              >
                Submit
              </Button>
              <Typography variant="body">
                I acknowledge and agree that BBC will generate shipments based upon reported wholesaler inventory and forecasted depletions, subject to change pending transportation and product availability.
              </Typography>
              {sending && <LinearProgress size={24} />}
            </Stack>
          </Box>
        </Box>
      ) : (
        <ForecastReviewGrid editableWeeksStartIndex={editableWeeksStartIndex} editableWeeksEndIndex={editableWeeksEndIndex} startWeek={startWeek} endWeek={endWeek} isHistorical={false} />
      )}
      <ImportSuccessDialog
        open={importSuccessDialogOpen}
        onClose={() => setImportSuccessDialogOpen(false)}
        successData={importSuccessData}
      />
    </React.Fragment >
  );
};

ForecastReview.propTypes = {
  editableWeeksStartIndex: PropTypes.number,
  editableWeeksEndIndex: PropTypes.number,
  startWeek: PropTypes.number,
  endWeek: PropTypes.number
}